import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { parseError } from 'hooks/shared/error';
import apiConfig from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  applyDemoArchiveState,
  archiveDemoNotificationId,
  isHardcodedInboxNotificationId,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/demoNotificationArchive';
import { getMemphisThreatNotification } from 'components/MapPage/CorporateViewTabs/NotificationsTab/memphisThreatNotification';
import { getEpaGulfThreatNotification } from 'components/MapPage/CorporateViewTabs/NotificationsTab/epaGulfThreatNotification';
import { getFacilityDemoPack } from 'components/MapPage/CorporateViewTabs/NotificationsTab/facilityDemoPack';
import { getFemaThunderboltInviteNotification } from 'components/MapPage/CorporateViewTabs/NotificationsTab/femaThunderboltInvite';
import { getUscgFocSoutheastThreatNotification } from 'components/MapPage/CorporateViewTabs/NotificationsTab/uscgFocSoutheastThreatNotification';
import {
  useFemaOpsBriefingFlag,
  useTennesseeMemphisDemoEnabled,
  useTropicalDemoKind,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import { NotificationType } from './useNotification';
import { ItemType } from 'constants/itemType';

export type UserNotificationDto = {
  userGuid: string;
  notificationId: string;
  groupId: string;
  archived: boolean;
  acknowledged: boolean;
  canArchive: boolean;
  createdAt: string;
  acknowledgedAt: string | null;
  archivedAt: string | null;
};

export enum NotificationActionType {
  ACTIVATION_REQUEST = 'activation_request',
}

export type ReviewerNotificationComment = {
  comment: string;
  reviewerName?: string | null;
  seatName?: string | null;
};

export type UserNotificationWithDetails = UserNotificationDto & {
  // Notification fields
  priority: string;
  status: string;
  acknowledgementRequired: boolean;
  sourceItemId: string;
  sourceItemType: ItemType;
  sentAt: string | null;
  scheduledDate: string | null;
  incidentId: string | null;
  aors: string[];

  // Metadata fields
  appMessage: string | null;
  emailSubject: string | null;
  emailMessage: string | null;
  smsMessage: string | null;
  teamsMessage: string | null;
  sendEmail: boolean | null;
  sendSms: boolean | null;
  sendTeams: boolean | null;
  actionType: NotificationActionType | null;
  /**
   * HWCG activation request the recipient can accept or decline in this inbox
   * item.
   */
  hwcgActivationRequest?: boolean;
  /** Roster replies pick a first available period. Group replies do not. */
  hwcgActivationSource?: 'roster' | 'group' | null;
  /** The operational period an open roster activation request is for. */
  hwcgActivationPeriodNumber?: number | null;
  reviewerComments?: ReviewerNotificationComment[];

  /** File Manager files/folders attached when the notification was composed. */
  files?: {
    fileId: string;
    name: string;
    kind: 'file' | 'folder';
    fileUrl: string | null;
    mimetype: string | null;
  }[];

  // Creator info
  createdByName: string | null;
  createdAt: string;
};

export const useUserNotifications = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const demoKind = useTropicalDemoKind();
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();
  const femaOps = useFemaOpsBriefingFlag();
  const uscgFocEnabled = useUscgFocRequirementsFlag();
  return useQuery<UserNotificationWithDetails[]>({
    queryKey: [
      'userNotifications',
      groupGuid,
      demoKind,
      tennesseeMemphisDemo,
      femaOps,
      uscgFocEnabled,
    ],
    queryFn: async () => {
      const response = await axios.get(apiConfig.apiGateway.userNotifications, {
        params: {
          groupId: groupGuid,
          notificationTypes: [NotificationType.IN_APP],
        },
      });
      const data = (response.data ?? []) as UserNotificationWithDetails[];
      if (!groupGuid) {
        return data;
      }
      const facilityPack = getFacilityDemoPack(demoKind);
      const demo = demoKind
        ? facilityPack
          ? facilityPack.getNotification(groupGuid)
          : demoKind === 'epa'
            ? getEpaGulfThreatNotification(groupGuid)
            : getMemphisThreatNotification(groupGuid, 'cfa')
        : tennesseeMemphisDemo
          ? getMemphisThreatNotification(groupGuid, 'tnng')
          : null;
      const extras: UserNotificationWithDetails[] = [];
      if (uscgFocEnabled) {
        extras.push(
          applyDemoArchiveState(
            getUscgFocSoutheastThreatNotification(groupGuid)
          )
        );
      }
      if (demo) extras.push(applyDemoArchiveState(demo));
      if (femaOps) {
        extras.push(
          applyDemoArchiveState(getFemaThunderboltInviteNotification(groupGuid))
        );
      }
      if (extras.length === 0) {
        return data;
      }
      const extraIds = new Set(extras.map((item) => item.notificationId));
      return [
        ...extras,
        ...data.filter((item) => !extraIds.has(item.notificationId)),
      ];
    },
    enabled: !!groupGuid,
  });
};

export const useAcknowledgeNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notificationId: string }) => {
      if (isHardcodedInboxNotificationId(data.notificationId)) {
        return;
      }
      return await axios.post(
        `${apiConfig.apiGateway.userNotifications}/${data.notificationId}/acknowledge`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useArchiveNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notificationId: string }) => {
      if (isHardcodedInboxNotificationId(data.notificationId)) {
        archiveDemoNotificationId(data.notificationId);
        return;
      }
      return await axios.post(
        `${apiConfig.apiGateway.userNotifications}/${data.notificationId}/archive`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};
